import { useState, useRef, useEffect } from 'react'
import { Textarea } from '@/components/ui/textarea'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Loader2Icon } from 'lucide-react'

interface SmartPhrase {
  id: number
  code: string
  name: string
  content: string
  is_system: boolean
}

interface SmartPhraseTextareaProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  rows?: number
  disabled?: boolean
}

export function SmartPhraseTextarea({
  value,
  onChange,
  placeholder = 'Type a template code (e.g., .FOLLOWUP2WK) or enter text...',
  rows = 6,
  disabled = false,
}: SmartPhraseTextareaProps) {
  const [suggestions, setSuggestions] = useState<SmartPhrase[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [loading, setLoading] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const suggestionsRef = useRef<HTMLDivElement>(null)

  // Detect when user types a template code pattern
  useEffect(() => {
    const detectTemplateCode = async () => {
      const cursorPosition = textareaRef.current?.selectionStart ?? value.length
      const textBeforeCursor = value.substring(0, cursorPosition)

      // Match pattern: dot followed by uppercase letters/numbers (e.g., .FOLLOWUP2WK)
      const match = textBeforeCursor.match(/\.([A-Z0-9]+)$/i)

      if (match) {
        const code = match[0] // includes the dot

        if (code.length >= 2) {
          // Search for templates that start with this code
          setLoading(true)
          try {
            const token = localStorage.getItem('auth_token')
            const response = await fetch('/admin/smart_phrases.json', {
              headers: {
                Authorization: `Bearer ${token}`,
                Accept: 'application/json',
              },
            })

            if (response.ok) {
              const data = await response.json()
              const matches = data.filter((phrase: SmartPhrase) =>
                phrase.code.toUpperCase().startsWith(code.toUpperCase())
              )

              if (matches.length > 0) {
                setSuggestions(matches)
                setShowSuggestions(true)
                setSelectedIndex(0)
              } else {
                setShowSuggestions(false)
              }
            }
          } catch (error) {
            console.error('Failed to fetch templates:', error)
          } finally {
            setLoading(false)
          }
        }
      } else {
        setShowSuggestions(false)
      }
    }

    detectTemplateCode()
  }, [value])

  // Handle template selection
  const insertTemplate = (phrase: SmartPhrase) => {
    const cursorPosition = textareaRef.current?.selectionStart ?? value.length
    const textBeforeCursor = value.substring(0, cursorPosition)
    const textAfterCursor = value.substring(cursorPosition)

    // Find and replace the partial code with the full template content
    const match = textBeforeCursor.match(/\.([A-Z0-9]+)$/i)
    if (match) {
      const codeStart = textBeforeCursor.lastIndexOf(match[0])
      const newText =
        value.substring(0, codeStart) + phrase.content + textAfterCursor

      onChange(newText)
      setShowSuggestions(false)

      // Focus back on textarea
      setTimeout(() => {
        textareaRef.current?.focus()
      }, 0)
    }
  }

  // Handle keyboard navigation in suggestions
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!showSuggestions) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex((prev) => (prev + 1) % suggestions.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex((prev) => (prev - 1 + suggestions.length) % suggestions.length)
    } else if (e.key === 'Enter' && suggestions.length > 0) {
      e.preventDefault()
      insertTemplate(suggestions[selectedIndex])
    } else if (e.key === 'Escape') {
      setShowSuggestions(false)
    }
  }

  return (
    <div className="relative">
      <Textarea
        ref={textareaRef}
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        className="font-mono text-sm"
      />

      {/* Template Code Suggestions */}
      {showSuggestions && suggestions.length > 0 && (
        <Card
          ref={suggestionsRef}
          className="absolute z-50 mt-1 w-full max-h-64 overflow-y-auto shadow-lg"
        >
          <div className="p-2">
            <div className="text-xs text-muted-foreground mb-2 px-2">
              Press Enter to insert, ↑↓ to navigate, Esc to cancel
            </div>
            {suggestions.map((phrase, index) => (
              <button
                key={phrase.id}
                type="button"
                className={`w-full text-left p-2 rounded hover:bg-accent transition-colors ${
                  index === selectedIndex ? 'bg-accent' : ''
                }`}
                onClick={() => insertTemplate(phrase)}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="secondary" className="font-mono text-xs">
                    {phrase.code}
                  </Badge>
                  {phrase.is_system && (
                    <Badge variant="default" className="text-xs">
                      System
                    </Badge>
                  )}
                </div>
                <div className="text-sm font-medium">{phrase.name}</div>
                <div className="text-xs text-muted-foreground line-clamp-2 mt-1">
                  {phrase.content}
                </div>
              </button>
            ))}
          </div>
        </Card>
      )}

      {/* Loading Indicator */}
      {loading && (
        <div className="absolute right-3 top-3">
          <Loader2Icon className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      )}
    </div>
  )
}
